import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import { exportStockTransferReportToPDF } from "../../utils/exportStockTransferReport";

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const StockTransferReport = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [shopId, setShopId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

 const isSuperAdmin = useMemo(
    () =>
      Boolean(
        auth?.user?.user?.is_superuser ||
          auth?.user?.is_superuser
      ),
    [auth]
  );

  const selectedBranchId = auth?.user?.selected_branch || "";

  const { data: shopsData } = useFetchObjects(["report-shops-stock-transfer"], "shop/shop/?page_size=all", token);
  const { data: transfersData, isLoading, isError, refetch } = useFetchObjects(
    ["report-stock-transfers", shopId, startDate?.format("YYYY-MM-DD"), endDate?.format("YYYY-MM-DD"), search],
    `shop/stock-transfers/?page_size=all${shopId ? `&supplier_branch=${shopId}` : ""}${startDate ? `&start_date=${startDate.format("YYYY-MM-DD")}` : ""}${endDate ? `&end_date=${endDate.format("YYYY-MM-DD")}` : ""}${search ? `&search=${encodeURIComponent(search)}` : ""}`,
    token
  );
  const { data: stockData } = useFetchObjects(["report-stock-transfer-stock-rows"], "shop/stocks/?page_size=all", token);
  const { data: accountTransactionsData } = useFetchObjects(["report-stock-transfer-payment-history"], "shop/account-transactions/?page_size=all", token);

  const shops = shopsData?.results || shopsData || [];
  const transfers = transfersData?.results || transfersData || [];
  const stockRows = stockData?.results || stockData || [];
  const accountTransactions = accountTransactionsData?.results || accountTransactionsData || [];

  const getTransferItemSalePrice = (item) => {
    const directPrice = Number(
      item?.sale_price ??
        item?.retail_price ??
        item?.purchase_item_details?.retail_price ??
        item?.purchase_item_details?.sale_price ??
        item?.source_stock_details?.retail_price ??
        item?.source_stock_details?.sale_price ??
        0
    );
    if (Number.isFinite(directPrice) && directPrice > 0) return directPrice;

    const stockRow =
      stockRows.find(
        (row) =>
          String(row?.purchase_item_details?.product?.id) ===
            String(item?.product?.id || item?.product_details?.id || item?.product || "") ||
          String(row?.purchase_item_details?.product?.id) ===
            String(item?.purchase_item_details?.product?.id || "")
      ) ||
      null;
    const fallbackPrice = Number(
      stockRow?.purchase_item_details?.retail_price ??
        stockRow?.purchase_item_details?.sale_price ??
        stockRow?.source_stock_details?.retail_price ??
        stockRow?.source_stock_details?.sale_price ??
        stockRow?.sale_price ??
        stockRow?.retail_price ??
        0
    );
    return Number.isFinite(fallbackPrice) ? fallbackPrice : 0;
  };

  const getRowTotalAmount = (row) => {
    const qty = Number(row?.transfer_quantity) || 0;
    const salePrice = getTransferItemSalePrice(row);
    const computed = qty * salePrice;
    if (computed > 0) return computed;
    const explicitTotal = row?.total_amount ?? row?.transfer_total_amount;
    if (explicitTotal != null && explicitTotal !== "") {
      const total = Number(explicitTotal);
      return Number.isFinite(total) ? total : 0;
    }
    return 0;
  };

  const getRowPaidAmount = (row) => {
    const transferId = row?.transfer_id;
    if (!transferId) return 0;
    return accountTransactions.reduce((sum, txn) => {
      const desc = String(txn?.description || "");
      if (
        txn?.transaction_type === "credit" &&
        desc.includes(String(transferId)) &&
        Number(txn?.amount) > 0
      ) {
        return sum + Number(txn.amount);
      }
      return sum;
    }, 0);
  };

  const getRowRemainedAmount = (row) => {
    const total = getRowTotalAmount(row);
    const paid = getRowPaidAmount(row);
    return Math.max(total - paid, 0);
  };

  const safeTransfers = useMemo(() => {
    const all = transfers.filter(Boolean);
    if (isSuperAdmin) return all;
    return all.filter(
      (row) =>
        String(row.supplier_branch) === String(selectedBranchId) ||
        String(row.customer_branch) === String(selectedBranchId)
    );
  }, [transfers, isSuperAdmin, selectedBranchId]);

  const filteredTransfers = useMemo(() => {
    return safeTransfers.filter((row) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        row.product_details?.name?.toLowerCase().includes(q) ||
        row.supplier_branch_details?.name?.toLowerCase().includes(q) ||
        row.customer_branch_details?.name?.toLowerCase().includes(q) ||
        String(toNumber(row.discount_amount ?? row.transfer_discount_amount)).includes(q) ||
        String(toNumber(row.total_amount ?? row.transfer_total_amount)).includes(q) ||
        String(toNumber(row.remained_amount ?? row.total_remained_amount)).includes(q)
      );
    });
  }, [safeTransfers, search]);

  const summary = useMemo(() => {
    const totalQty = filteredTransfers.reduce((sum, row) => sum + (Number(row.transfer_quantity) || 0), 0);
    const totalDiscount = filteredTransfers.reduce(
      (sum, row) => sum + toNumber(row.discount_amount ?? row.transfer_discount_amount),
      0
    );
    const totalAmount = filteredTransfers.reduce(
      (sum, row) => sum + getRowTotalAmount(row),
      0
    );
    const totalRemained = filteredTransfers.reduce(
      (sum, row) => sum + getRowRemainedAmount(row),
      0
    );
    return {
      totalTransfers: filteredTransfers.length,
      totalQty,
      totalDiscount,
      totalAmount,
      totalRemained,
    };
  }, [filteredTransfers, stockRows, accountTransactions]);

  const handleExport = (type) => {
    if (type === "pdf") exportStockTransferReportToPDF(filteredTransfers);
    setAnchorEl(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ height: "100vh", display: "flex", justifyContent: "center", width: "100%" }}>
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{t("reports.error_fetching_data", "Error fetching data")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h5">Stock Transfer Report</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("payroll.dataLoadedOn")} {new Date().toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button variant="contained" onClick={(e) => setAnchorEl(e.currentTarget)}>
              Export
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => handleExport("pdf")}>Export PDF</MenuItem>
            </Menu>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {isSuperAdmin && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <Select value={shopId} onChange={(e) => setShopId(e.target.value)} displayEmpty>
                  <MenuItem value="">All Branches</MenuItem>
                  {shops.map((shop) => (
                    <MenuItem key={shop.id} value={String(shop.id)}>
                      {shop.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={isSuperAdmin ? 3 : 4}>
            <DatePicker label="Start Date" value={startDate} onChange={setStartDate} slotProps={{ textField: { fullWidth: true } }} />
          </Grid>
          <Grid item xs={12} md={isSuperAdmin ? 3 : 4}>
            <DatePicker label="End Date" value={endDate} onChange={setEndDate} slotProps={{ textField: { fullWidth: true } }} />
          </Grid>
          <Grid item xs={12} md={isSuperAdmin ? 3 : 4}>
            <TextField fullWidth placeholder="Search transfers" value={search} onChange={(e) => setSearch(e.target.value)} />
          </Grid>
        </Grid>
      </LocalizationProvider>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>Product</TableCell>
              <TableCell sx={{ color: "white" }}>From Branch</TableCell>
              <TableCell sx={{ color: "white" }}>To Branch</TableCell>
              <TableCell sx={{ color: "white" }}>Qty</TableCell>
              <TableCell sx={{ color: "white" }}>Date</TableCell>
              <TableCell sx={{ color: "white" }}>Status</TableCell>
              <TableCell sx={{ color: "white" }}>User</TableCell>
              <TableCell sx={{ color: "white" }}>Discount Amount</TableCell>
              <TableCell sx={{ color: "white" }}>Total Amount</TableCell>
              <TableCell sx={{ color: "white" }}>Remained Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransfers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No stock transfer records found.
                </TableCell>
              </TableRow>
            ) : (
              filteredTransfers.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.product_details?.name || "-"}</TableCell>
                  <TableCell>{row.supplier_branch_details?.name || "-"}</TableCell>
                  <TableCell>{row.customer_branch_details?.name || "-"}</TableCell>
                  <TableCell>{row.transfer_quantity}</TableCell>
                  <TableCell>{row.transfer_date}</TableCell>
                  <TableCell>{row.status_display || row.status || "-"}</TableCell>
                  <TableCell>{row.transfer_user_details?.username || "-"}</TableCell>
                  <TableCell>{toNumber(row.discount_amount ?? row.transfer_discount_amount).toFixed(2)}</TableCell>
                  <TableCell>{getRowTotalAmount(row).toFixed(2)}</TableCell>
                  <TableCell>{getRowRemainedAmount(row).toFixed(2)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow sx={{ backgroundColor: "#f7f7f7", "& td": { fontWeight: 700 } }}>
              <TableCell colSpan={7}>Footer Total</TableCell>
              <TableCell>{summary.totalDiscount.toFixed(2)}</TableCell>
              <TableCell>{summary.totalAmount.toFixed(2)}</TableCell>
              <TableCell>{summary.totalRemained.toFixed(2)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StockTransferReport;
